import {EWedletEditMode, EWedletEditModeLabel, IElementWedlet, IWedlet, WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {IReg, IUiEnv} from "/@lib@/commons/registry.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {Button} from "/@back@/commons/widgets/buttons.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {CidSession, ECidSessionState, OCidSessionConfig} from "/@lib@/core/cid.js";
import {CidInteractionByStepsUi, ICidCtx, OCidInteractionByStepsUiInit} from "/@back@/core/widgets/cidUi.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {IJmlNode} from "/@lib@/commons/xml/jml.js";
import {IUniverseEnv} from "/@lib@/core/universe.js";
import {IO} from "/@lib@/commons/io/io.js";
import {CDM} from "/@lib@/commons/utils/cdm.js";
import {IXmlMsg} from "/@lib@/edit/ot/xmlHouse.js";
import {LANG} from "/@lib@/commons/lang.js";
import {REMOTE} from "/@lib@/core/remote.js";

/**
 * Déclenche une intéraction cid dans un contexte wed
 * Contexte : bind du noeud texte à modifier
 *
 * <box-cid-action  label="xxx"
 *                  title=yyy yyy yyy"
 *                  icon=":wspRes:editors/xxx.png"
 *                  visiblePerms="perm1"
 *                  enablePerms="perm2"
 *                  cidProcessIs="aaa bbb ccc" deprecated au profit du tag 'cidHosts' : FIXME 5.1 : à supprimer
 *                  cidHostIs="111 222 333" deprecated au profit du tag 'cidProcesses' : FIXME 5.1 : à supprimer
 *                  cidManifestUrlInput?="false"
 *                  cidPassOptionalInteraction?="false"
 *                  cidHostUse?="both"
 *                  cidMetaIn?="publicUrl"
 *                  cidMetaOut?="publicUrl"
 *                  injectAlias?="false">
 *    <cidHosts>
 *			<cidHostIs>aaa bbb</cidHosts>
 *			<cidHostIs>ccc</cidHosts>
 *    </cidHosts>
 *    <cidProcesses>
 *			<cidProcessIs>aaa bbb</cidHosts>
 *			<cidProcessIs>ccc</cidHosts>
 *    </cidProcesses>
 *    <filePicker? accept=""/> : si renseigné, l'écran de sélection de fichier est affiché
 *    <meta* code="" value=""/>
 * </box-cid-action>
 *
 */
export class BoxCidAction extends HTMLElement implements IElementWedlet {

	protected _button: Button;

	protected url: string;

	wedlet: IWedlet;

	reg: IReg<IUiEnv & IUniverseEnv>

	visiblePerms?: string[];

	enablePerms?: string[];

	protected metas: Map<string, string> = new Map();

	protected filePickerElt: HTMLInputElement;

	protected fileToUpload: File;

	cidProcessIs?: string[] | string[][];

	cidHostIs?: string[] | string[][];

	injectAlias?: boolean;

	cidManifestUrlInput?: boolean;

	cidPassOptionalInteraction?: boolean;

	cidMetaIn: string = "publicUrl";

	cidMetaOut: string = "publicUrl";

	/** Filtre des hosts par rapport au "use"
	 * 'both' par défaut
	 **/
	cidHostUse: 'input' | 'output' | 'both' = "both";

	get label(): string {return this.getAttribute("label")}

	get icon(): string {return this.getAttribute("icon")}

	protected async doBtnAction(this: HTMLButtonElement) {
		if (this.disabled) return;
		const me = DOMSH.findHost<BoxCidAction>(this);
		if (me.filePickerElt) me.filePickerElt.click();
		else me.doCidProcess()
	}

	protected onSelectFile(this: HTMLInputElement) {
		const me = DOMSH.findHost<BoxCidAction>(this);
		me.fileToUpload = this.files ? this.files.item(0) : null;
		this.value = null;
		if (me.fileToUpload) me.doCidProcess();
	}

	protected async doCidProcess() {
		const ct = <CidInteractionByStepsUi/>;
		// Meta
		ct.overrideMetas = async (ctx: ICidCtx, metasIn: Dict<any>, foreignMetas: { is: string[], value: string | ((session: CidSession) => string) }[]) => {
			if (this.cidMetaIn) {
				const url = await REMOTE.migrateUrl(this.url, this.reg);
				if (url) metasIn[this.cidMetaIn] = url;
			}

			// Ajout du nom du fichier s'il s'agit d'un upload
			if (this.fileToUpload) metasIn['fileName'] = this.fileToUpload.name;

			if (this.metas && this.metas.size > 0) {
				this.metas.forEach((value: string, key: any) => {
					metasIn[key] = value;
				});
			}
		};

		const cidSessionConfig = {} as OCidSessionConfig;
		if (this.fileToUpload) {
			cidSessionConfig.bodyToUpload = await this.fileToUpload;
			/*cidSessionConfig.customUpload = async (session: CidSession, uploadStep: CidUploadStep, webUpload: ICidWebUploadDef): Promise<null | Response> => {
				session.config.bodyToUpload = await me.fileToUpload;
			}*/
		}

		ct.initialize({
			reg: this.reg,
			skinOver: "webzone:panel",
			cidProcessIs: this.cidProcessIs,
			cidHostIs: this.cidHostIs,
			cidManifestUrlInput: this.cidManifestUrlInput,
			cidPassOptionalInteraction: this.cidPassOptionalInteraction,
			cidHostUse: this.cidHostUse,
			cidSessionConfig: cidSessionConfig,
			onInteractionEndHandler: async (cidSession: CidSession) => {
				switch (cidSession.state) {
				case ECidSessionState.failed:
					await ERROR.showError({
						msg: "Échec d'interaction avec le serveur de ressources",
						details: cidSession.failedReason
					});
					break;
				case ECidSessionState.ended:
					//on ajoute
					const xa = this.wedlet.wedAnchor;
					const batch = this.wedlet.wedMgr.docHolder.newBatch();
					let value = cidSession.metasOut[this.cidMetaOut] || "";
					if (this.injectAlias) value = await REMOTE.aliasUrl(value, this.reg);
					if (this.wedlet.isVirtual()) {
						WEDLET.insertDatasFromDisplay(this.wedlet, batch, value);
					} else {
						batch.setText(xa, value);
					}
					batch.doBatch();
				}
				result.close();
			}
		} as OCidInteractionByStepsUiInit);

		const result = POPUP.showDialog(ct, this.reg.env.uiRoot, {
			titleBar: {barLabel: {label: this.label}, closeButton: {}},
			initWidth: '70vw',
			initHeight: '70vh',
			resizer: {}
		});

	}

	// IElementWedlet
	configWedletElt(tpl: Element, wedlet: IWedlet) {
		this.wedlet = wedlet;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		WEDLET.installSkins(tpl, sr, wedlet, this.localName);
		this.reg = wedlet.wedMgr.reg;

		if (!this.visiblePerms || this.reg.hasPerm(this.visiblePerms)) {
			this._button = sr.appendChild(this.createButton());
			this._button.disabled = (this.enablePerms && !this.reg.hasPerm(this.enablePerms)) || !WEDLET.isWritableWedlet(this.wedlet);
		}

		const metaElts = tpl.querySelectorAll("meta");
		metaElts.forEach((metaElt => {
			this.metas.set(metaElt.getAttribute("code"), metaElt.getAttribute("value"));
		}));

		const filePicker = tpl.querySelector("filePicker");
		if (filePicker) {
			this.filePickerElt = <input type="file" accept={filePicker.getAttribute("accept")} onchange={this.onSelectFile} style="display:none;"/> as HTMLInputElement;
			sr.appendChild(this.filePickerElt);
		}

		const cidProcessIsElts = tpl.querySelectorAll("cidProcesses > cidProcessIs");
		if (cidProcessIsElts && cidProcessIsElts.length > 0) {
			this.cidProcessIs = [];
			cidProcessIsElts.forEach((entry) => {
				if (entry.textContent) (this.cidProcessIs as string[][]).push(entry.textContent.split(" "));
			});
		} else if (tpl.hasAttribute("cidProcessIs")) {
			/*@deprecated 5.1*/
			this.cidProcessIs = tpl.getAttribute("cidProcessIs").split(" ")
		}

		const cidHostIsElts = tpl.querySelectorAll("cidHosts > cidHostIs");
		if (cidHostIsElts && cidHostIsElts.length > 0) {
			this.cidHostIs = [];
			cidHostIsElts.forEach((entry) => {
				if (entry.textContent)
					(this.cidHostIs as string[][]).push(entry.textContent.split(" "));
			});
		} else if (tpl.hasAttribute("cidHostIs")) {
			/*@deprecated 5.1*/
			this.cidHostIs = tpl.getAttribute("cidHostIs").split(" ")
		}

		if (tpl.hasAttribute("injectAlias")) this.injectAlias = tpl.getAttribute("injectAlias") == "true";

		if (tpl.hasAttribute("cidManifestUrlInput")) this.cidManifestUrlInput = tpl.getAttribute("cidManifestUrlInput") == "true";

		if (tpl.hasAttribute("cidPassOptionalInteraction")) this.cidPassOptionalInteraction = tpl.getAttribute("cidPassOptionalInteraction") == "true";

		if (tpl.hasAttribute("cidHostUse")) this.cidHostUse = tpl.getAttribute("cidHostUse") as any;

		if (tpl.hasAttribute("cidMetaIn")) this.cidMetaIn = tpl.getAttribute("cidMetaIn");

		if (tpl.hasAttribute("cidMetaOut")) this.cidMetaOut = tpl.getAttribute("cidMetaOut");

		if (tpl.hasAttribute("visiblePerms")) this.visiblePerms = tpl.getAttribute("visiblePerms").split(" ");

		if (tpl.hasAttribute("enablePerms")) this.enablePerms = tpl.getAttribute("enablePerms").split(" ");
	}

	protected createButton(): Button {
		return <Button onclick={this.doBtnAction} label={this.label} title={this.title} icon={this.icon}/> as Button
	}

	refreshBindValue(val: IJmlNode) {
		this.url = val as string;
		WEDLET.clearAnnots(this);
	}

	insertChars(from: number, chars: string, msg: IXmlMsg) {
		this.refreshBindValue(LANG.stringInsert(this.url, from, chars));
	}

	deleteChars(from: number, len: number, msg: IXmlMsg) {
		this.refreshBindValue(LANG.stringDelete(this.url, from, len));
	}

	replaceChars(chars: string, msg: IXmlMsg) {
		this.refreshBindValue(chars);
	}

	setEditMode(mode: EWedletEditMode) {
		DOM.setAttr(this, "edit-mode", EWedletEditModeLabel[mode]);
		this.reassessPerms();
	}

	reassessPerms() {
		if (this.visiblePerms && !this.reg.hasPerm(this.visiblePerms)) {
			if (this._button) {
				this._button.remove();
				this._button = null;
			}
		} else {
			if (!this._button) this._button = this.shadowRoot.appendChild(this.createButton());
		}
		if (this._button) {
			this._button.disabled =
				this.getAttribute("edit-mode") !== EWedletEditModeLabel[EWedletEditMode.write] //XXX pb si action CID non directement liée à l'édition : consultation, export...
				|| (this.enablePerms && !this.reg.hasPerm(this.enablePerms));
		}
	}
}

window.customElements.define("box-cid-action", BoxCidAction);
